import type { Opt } from '@jet/environment';
import type { Intent } from '@jet/environment/dispatching';
import type {
    PageEventTrigger,
    PageInvalidationRules,
} from '../../models/invalidation/InvalidationRules';
import type { Page } from '../../models/page';
import { makeRefreshPageIntent } from '../refresh-page/RefreshPageIntent';

/**
 * Construct event triggers for expiration change
 *
 * @param pageIntent
 * @param expirationDate
 * @returns
 */
export function pageExpirationEventTrigger(
    pageIntent: Intent<Page>,
    expirationDate?: Opt<Date>,
): Opt<PageEventTrigger> {
    if (!expirationDate) {
        return null;
    }

    return {
        events: [
            {
                name: 'expirationDate',
                date: expirationDate.toJSON(),
            },
        ],
        intent: makeRefreshPageIntent(pageIntent, 'Exceeded expiration date'),
        deferIntentUntilEvents: ['nextPageDisappearance'],
    };
}

/**
 * Construct event triggers for Subscription change
 *
 * @param pageIntent
 * @returns
 */
export function pageSubscriptionStatusChangeEventTrigger(
    pageIntent: Intent<Page>,
): PageEventTrigger {
    return {
        events: [{ name: 'subscriptionStatusChange' }],
        intent: makeRefreshPageIntent(
            pageIntent,
            'Subscription status changed',
        ),
    };
}

export function pageSortByChangeEventTrigger(
    pageIntent: Intent<Page>,
): PageEventTrigger {
    return {
        events: [{ name: 'sortByChanged' }],
        intent: makeRefreshPageIntent(pageIntent, 'Sorting'),
    };
}

/**
 * Construct base page invalidation rules for pages
 *
 * @param pageIntent
 * @param includeSubscriptionStatusChangeTrigger
 * @param expirationDate
 * @returns
 */
export function pageInvalidationsRules(
    pageIntent: Intent<Page>,
    includeSubscriptionStatusChangeTrigger: boolean,
    expirationDate?: Opt<Date>,
): Opt<PageInvalidationRules> {
    const eventTriggers: PageEventTrigger[] = [];

    if (includeSubscriptionStatusChangeTrigger) {
        eventTriggers.push(
            pageSubscriptionStatusChangeEventTrigger(pageIntent),
        );
    }

    const expirationEventTrigger = pageExpirationEventTrigger(
        pageIntent,
        expirationDate,
    );

    if (expirationEventTrigger) {
        eventTriggers.push(expirationEventTrigger);
    }

    if (eventTriggers.length === 0) {
        return null;
    }

    return {
        eventTriggers,
    };
}
